/**
 * Roleplay stage palette of the default brand — the dark "immersive session" skin, deliberately kept
 * SEPARATE from the main brand palette: that one is a
 * light document palette and none of its hues exist here.
 *
 * Why a TS module rather than more `--rpv2-*` CSS variables: those variables are
 * declared on `.rpv2-root` (see `roleplay.css` in this pack), but the busiest
 * colour consumers — the startup overlay, the startup error screen and the audio-gate
 * banner — render OUTSIDE that element (straight under the fixed wrapper in
 * `Roleplay.tsx`), and the policy print view writes its markup into a blank window that
 * carries no stylesheet at all. `var(--rpv2-…)` silently resolves to nothing in those
 * places, so their colours have to travel as values.
 *
 * The first block mirrors the `--rpv2-*` scale by value — change one, change both.
 * Everything after it only ever existed as inline literals in the components named in
 * each comment. Same shape as the landing and main palettes: a solid map and
 * an RGB-channel map for the alpha variants.
 */

/** Solid colors (hex) */
export const rpColors = {
  // ── Mirrors the --rpv2-* tokens in roleplay.css (keep in sync by value) ──
  bg1: '#0A0B1E',
  bg2: '#13152E',
  surface: '#1A1D3A',
  primary: '#7B5FFF',
  primary2: '#5B3FFF',
  success: '#22D38F',
  danger: '#FF4D6D',
  warning: '#FFB547',
  text: '#E6E8FF',

  // ── Accents (button and progress gradients, thinking indicator, persona chrome) ──
  primaryLight: '#9A7EFF',
  accentRampMid: '#A06BFF',
  accentRampEnd: '#C879FF',
  accentText: '#B9A8FF',
  startupAccent: '#C4B5FD',
  iconHighlight: '#B79BFF',
  titleGradientStart: '#A5B4FC',
  titleGradientMid: '#C084FC',
  titleGradientEnd: '#F0ABFC',
  countdownBarEnd: '#FF5CC7',
  reportGradientEnd: '#FF7BD1',
  /** Avatar placeholder on the tablet header. */
  avatarPlaceholder1: '#6A5A8C',
  avatarPlaceholder2: '#3A2C54',

  // ── Info accent used by the startup + error screens ──
  info: '#0E94C9',
  infoBright: '#1BB6E7',

  // ── Status alternates. These are NOT duplicates of danger/warning/success
  // above — the startup screens shipped with their own shades and matching them
  // to the core scale would change what is on screen today.
  dangerAlt: '#EF4444',
  dangerBright: '#FF5A78',
  dangerSoft: '#FF7D96',
  dangerDeep: '#E83D5D',
  dangerLight: '#F87171',
  dangerPale: '#FECACA',
  dangerTint: '#FFB4C3',
  warningAlt: '#F5B400',
  warningVivid: '#F59E0B',
  warningDeep: '#D97706',
  warningBright: '#FBBF24',
  warningButtonStart: '#FFD257',
  warningText: '#FFCF8A',
  warningPale: '#FDE0AB',
  successAlt: '#10B981',
  successLight: '#34D399',

  // ── Near-black surface stops. Each is a gradient stop or scrim owned by one
  // surface; named after that surface so a change stays traceable.
  overlayInk: '#06081A', // startup overlay + startup error backdrop
  startupCardInk: '#1F2042', // startup overlay card
  errorInk: '#0E0A18', // startup error screen
  errorInkWash: '#2A1224', // startup error screen, warm wash
  curtainInk: '#050612', // countdown-end + report-generating curtain
  curtainInk2: '#0F0F28',
  curtainInk3: '#1F1C46',
  railInk: '#0F0F26', // tablet left rail
  columnInk: '#0F1126', // desktop conversation column + side-panel tabbar
  stageInk: '#0E1126', // avatar stage
  dialogInk: '#141732', // end-confirm + idle-warning dialogs
  headerInk: '#1A142A', // mobile + tablet headers
  barInk: '#2C2A4A', // mobile + tablet bottom bars
  dangerInk: '#3C0E1A', // error banner + desktop error strip
  dangerInkDeep: '#280A14', // policy view error state
  connectionInk: '#260C16', // connection-lost banner
  connectionInk2: '#1C0812',
  connectionWarnInk: '#281C08', // connection-lost banner, warning variant
  connectionWarnInk2: '#1E1404',
  audioWarnInk: '#1A1405', // audio-blocked banner
  audioWarnInk2: '#1E1606',
  audioWarnInk3: '#302408',
  warnInk: '#3C320E', // error banner, warning variant

  textBright: '#FFFFFF',

  // ── Status text on the stage; mirror --rpv2-*-label. ──
  successLabel: '#22D38F',
  warningLabel: '#FFB547',
  dangerLabel: '#FF4D6D',
} as const;

/** RGB channels for the colors that are used with an alpha. */
export const rpRgb = {
  primary: '123,95,255',
  primary2: '91,63,255',
  success: '34,211,143',
  danger: '255,77,109',
  warning: '255,181,71',
  text: '230,232,255',
  bg1: '10,11,30',
  bg2: '19,21,46',
  surface: '26,29,58',
  accentRampMid: '160,107,255',
  accentRampEnd: '200,121,255',
  accentText: '185,168,255',
  info: '14,148,201',
  infoBright: '27,182,231',
  dangerAlt: '239,68,68',
  dangerBright: '255,90,120',
  dangerPale: '254,202,202',
  dangerTint: '255,180,195',
  warningAlt: '245,180,0',
  warningVivid: '245,158,11',
  warningPale: '253,224,171',
  successAlt: '16,185,129',
  overlayInk: '6,8,26',
  curtainInk: '5,6,18',
  curtainInk2: '15,15,40',
  curtainInk3: '31,28,70',
  railInk: '15,15,38',
  columnInk: '15,17,38',
  dialogInk: '20,23,50',
  dangerInk: '60,14,26',
  dangerInkDeep: '40,10,20',
  connectionInk: '38,12,22',
  connectionInk2: '28,8,18',
  connectionWarnInk: '40,28,8',
  connectionWarnInk2: '30,20,4',
  audioWarnInk2: '30,22,6',
  audioWarnInk3: '48,36,8',
  warnInk: '60,50,14',
  textBright: '255,255,255',
  // Channels below have no solid of the same name.
  particle: '192,132,252',
  dangerGlow: '255,123,144',
  warningBannerTint: '245,166,35',
  glowWarm: '236,72,153',
  highlight: '255,255,255',
  shade: '0,0,0',
} as const;
